import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';

function EventTypeList() {
  const { 
    getEventTypes, 
    deleteEventType, 
    isLoading, 
    generateCacheKey 
  } = useData();
  
  const [eventTypes, setEventTypes] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const cacheKey = generateCacheKey('event-types');
  const loading = isLoading(cacheKey);

  const loadEventTypes = useCallback(async () => {
    try {
      setError(null);
      const data = await getEventTypes();
      console.log('Event types loaded from DataContext:', data);
      setEventTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading event types:', error);
      setError('Failed to load event types. Please try again.');
    }
  }, [getEventTypes]);

  useEffect(() => {
    loadEventTypes();
  }, [loadEventTypes]);

  const handleDelete = async (eventTypeId, name) => {
    if (!window.confirm(`Are you sure you want to delete the event type "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setError(null);
      await deleteEventType(eventTypeId);
      // Refresh the list
      await loadEventTypes();
      console.log('Event type deleted successfully');
    } catch (error) {
      console.error('Error deleting event type:', error);
      setError('Failed to delete event type. Please try again.');
    }
  };

  const filteredEventTypes = eventTypes.filter(type =>
    type.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    type.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading event types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-theme-panel" style={{display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center' }}>
        <h2 style={{margin:0,fontSize:'1.1rem'}}>Event Types</h2>
        <Link to="/settings/event-types/create">
          <button type="button" className="btn-primary btn-sm">Create New Type</button>
        </Link>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div>
        <input
          type="text"
          placeholder="Search event types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="tp-input"
          style={{width:'100%',maxWidth:'400px'}}
        />
      </div>

      {filteredEventTypes.length === 0 ? (
        <div className="empty-state">
          <h3>No event types found</h3>
          <p>{searchTerm ? 'No event types match your search.' : 'No event types found. Create your first one!'}</p>
        </div>
      ) : (
        <div className="settings-theme-table-wrapper"><table className="settings-theme-table">
          <thead>
            <tr>
              <th>Type Name</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEventTypes.map((type, index) => (
              <tr key={type.eventTypeId || `event-type-${index}`}>
                <td data-label="Type Name">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {type.color && (
                      <div 
                        style={{
                          width: '16px',
                          height: '16px',
                          backgroundColor: type.color,
                          borderRadius: '3px',
                          border: '1px solid var(--border-subtle)'
                        }}
                        title={`Color: ${type.color}`}
                      />
                    )}
                    <strong>{type.name || 'Unnamed Type'}</strong>
                  </div>
                </td>
                <td data-label="Description">{type.description || 'No description'}</td>
                <td data-label="Status">
                  <span className={"settings-theme-badge" + (type.isActive !== false ? ' active' : '')}>{type.isActive !== false ? 'Active' : 'Inactive'}</span>
                </td>
                <td data-label="Actions">
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {type.eventTypeId ? (
                      <Link to={`/settings/event-types/edit/${type.eventTypeId}`} className="btn-link btn-sm">Edit</Link>
                    ) : (
                      <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                        No ID
                      </span>
                    )}
                    <button className="btn-danger btn-sm" onClick={() => handleDelete(type.eventTypeId || index, type.name)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      <div className="settings-theme-panel" style={{marginTop:'1.5rem',padding:'1rem'}}>
        <h4 style={{margin:'0 0 .5rem'}}>About Event Types</h4>
        <p style={{margin:0,fontSize:'.75rem',color:'var(--text-secondary)'}}>
          Event types define different categories of learning events you can track. Examples include lectures, 
          workshops, labs, exams, or study sessions. Each type can have its own color coding and custom metadata 
          fields to help you organize and categorize your educational activities.
        </p>
      </div>
    </div>
  );
}

export default EventTypeList;